
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { Button, Row, Text } from '@client/shared/design';
import { cx } from '@client/shared/lib/cx.ts';
import { useMediaQuery } from '@client/shared/hooks/use-media-query.ts';
import { useSlotPaint } from './use-slot-paint.ts';
import { cellKey, parseCellKey, type CellKey } from './cell-instants.ts';
import styles from './SlotGrid.module.css';

const COARSE_POINTER_QUERY = '(hover: none) and (pointer: coarse)';

export function useSlotSelectionMode(override?: 'pointer' | 'tap'): 'pointer' | 'tap' {
  const coarse = useMediaQuery(COARSE_POINTER_QUERY);
  return override ?? (coarse ? 'tap' : 'pointer');
}

export type CellState = 'free' | 'booked' | 'unavailable' | 'selected' | 'disabled';

export interface SlotGridColumn {
  readonly date: string;
  readonly label: string;
  readonly isToday?: boolean;
}

export interface SlotGridRow {
  readonly slot: number;
  readonly label: string;
}

export interface SlotGridEvent {
  readonly id: string;
  readonly dayIndex: number;
  readonly startSlot: number;
  readonly endSlot: number;
  readonly content: ReactNode;
  readonly tone?: 'mine' | 'other' | 'blocked';
  readonly label?: string;
  readonly interactive?: boolean;
}

export interface SlotGridProps {
  ariaLabel: string;
  columns: readonly SlotGridColumn[];
  rows: readonly SlotGridRow[];
  painted: ReadonlySet<CellKey>;
  onChange: (next: Set<CellKey>) => void;
  cellState: (day: number, slot: number) => CellState;
  cellLabel: (day: number, slot: number, state: CellState) => string;
  zoneLabel: string;
  events?: readonly SlotGridEvent[];
  onPaintEnd?: (point: { x: number; y: number } | null) => void;
  selectionMode?: 'pointer' | 'tap';
  className?: string;
}

function eventToneClass(tone: SlotGridEvent['tone']): string | undefined {
  switch (tone) {
    case 'other':
      return styles.eventOther;
    case 'blocked':
      return styles.eventBlocked;
    case 'mine':
    default:
      return undefined;
  }
}

function cellClass(state: CellState): string | undefined {
  switch (state) {
    case 'booked':
      return styles.cellBooked;
    case 'unavailable':
      return styles.cellUnavailable;
    case 'selected':
      return styles.cellSelected;
    case 'disabled':
      return styles.cellDisabled;
    case 'free':
    default:
      return undefined;
  }
}

interface SlotCellProps {
  readonly day: number;
  readonly slot: number;
  readonly state: CellState;
  readonly disabled: boolean;
  readonly label: string;
  readonly mode: 'pointer' | 'tap';
  readonly armed: boolean;
  readonly onDown: (day: number, slot: number) => void;
  readonly onEnter: (day: number, slot: number) => void;
  readonly onTap: (day: number, slot: number, point: { x: number; y: number }) => void;
}

const SlotCell = memo(function SlotCell({
  day,
  slot,
  state,
  disabled,
  label,
  mode,
  armed,
  onDown,
  onEnter,
  onTap,
}: SlotCellProps) {
  const onClick = useCallback(
    (e: ReactMouseEvent<HTMLButtonElement>) => {
      let point = { x: e.clientX, y: e.clientY };
      if (point.x === 0 && point.y === 0) {
        const r = e.currentTarget.getBoundingClientRect();
        point = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      }
      onTap(day, slot, point);
    },
    [day, slot, onTap],
  );
  return (
    <button
      type="button"
      role="gridcell"
      aria-pressed={state === 'selected'}
      aria-disabled={disabled}
      aria-label={armed ? `${label}: selection started, tap an end time` : label}
      data-armed={armed || undefined}
      className={cx(styles.cell, styles.slot, cellClass(state), armed && styles.cellArmed)}
      onMouseDown={mode === 'pointer' ? () => onDown(day, slot) : undefined}
      onMouseEnter={mode === 'pointer' ? () => onEnter(day, slot) : undefined}
      onClick={mode === 'tap' ? onClick : undefined}
    />
  );
});

export function SlotGrid({
  ariaLabel,
  columns,
  rows,
  painted,
  onChange,
  cellState,
  cellLabel,
  zoneLabel,
  events,
  onPaintEnd,
  selectionMode,
  className,
}: SlotGridProps) {
  const mode = useSlotSelectionMode(selectionMode);
  const firstSlot = rows[0]?.slot ?? 0;

  const cellStateRef = useRef(cellState);
  const onChangeRef = useRef(onChange);
  const onPaintEndRef = useRef(onPaintEnd);

  const paint = useSlotPaint({
    painted,
    canPaint: useCallback((key: CellKey) => {
      const { day, slot } = parseCellKey(key);
      const s = cellStateRef.current(day, slot);
      return s !== 'booked' && s !== 'unavailable' && s !== 'disabled';
    }, []),
  });
  const paintRef = useRef(paint);

  useEffect(() => {
    cellStateRef.current = cellState;
    onChangeRef.current = onChange;
    onPaintEndRef.current = onPaintEnd;
    paintRef.current = paint;
  });

  useEffect(() => {
    paintRef.current.cancelTap();
  }, [mode]);

  const finishStroke = useCallback((point: { x: number; y: number } | null) => {
    const wasPainting = paintRef.current.isPainting;
    paintRef.current.endStroke();
    if (wasPainting) onPaintEndRef.current?.(point);
  }, []);

  const onCellDown = useCallback((day: number, slot: number) => {
    onChangeRef.current(paintRef.current.beginStroke(cellKey(day, slot)));
  }, []);
  const onCellEnter = useCallback((day: number, slot: number) => {
    const next = paintRef.current.extendStroke(cellKey(day, slot));
    if (next) onChangeRef.current(next);
  }, []);

  const onCellTap = useCallback((day: number, slot: number, point: { x: number; y: number }) => {
    const p = paintRef.current;
    const armed = p.armedAnchor;
    const key = cellKey(day, slot);
    if (armed && armed.day === day) {
      const next = p.commitTap(key);
      if (next) {
        onChangeRef.current(next);
        onPaintEndRef.current?.(point);
      }
      return;
    }
    p.armTap(key);
  }, []);

  const armedAnchor = mode === 'tap' ? paint.armedAnchor : null;

  return (
    <div
      className={cx(styles.calendar, className)}
      onMouseLeave={mode === 'pointer' ? () => finishStroke(null) : undefined}
      onMouseUp={
        mode === 'pointer' ? (e) => finishStroke({ x: e.clientX, y: e.clientY }) : undefined
      }
      onKeyDown={
        mode === 'tap' && armedAnchor ? (e) => e.key === 'Escape' && paint.cancelTap() : undefined
      }
      data-painting={paint.isPainting || undefined}
      data-armed={armedAnchor ? '' : undefined}
    >
      {armedAnchor && (
        <Row justify="between" align="center" className={styles.legend}>
          <Text size="xs" tone="muted">
            Tap an end time: the same slot again selects just it
          </Text>
          <Button variant="secondary" size="sm" onClick={() => paint.cancelTap()}>
            Cancel
          </Button>
        </Row>
      )}

      <div className={styles.scroll}>
        <div
          className={styles.gridStack}
          style={{
            ['--day-count' as string]: String(columns.length),
            ['--row-count' as string]: String(rows.length),
          }}
        >
          <div className={styles.grid} role="grid" aria-label={ariaLabel}>
            <div className={cx(styles.cell, styles.corner, styles.mono)}>{zoneLabel}</div>
            {columns.map((col) => (
              <div
                key={col.date}
                className={cx(styles.cell, styles.dayHead, col.isToday && styles.dayHeadToday)}
                role="columnheader"
              >
                {col.label}
              </div>
            ))}

            {rows.map((row) => (
              <Fragment key={row.slot}>
                <div className={cx(styles.cell, styles.time, styles.mono)} role="rowheader">
                  {row.label}
                </div>
                {columns.map((col, dayIdx) => {
                  const key = cellKey(dayIdx, row.slot);
                  const base = cellState(dayIdx, row.slot);
                  const state: CellState = painted.has(key) ? 'selected' : base;
                  return (
                    <SlotCell
                      key={col.date}
                      day={dayIdx}
                      slot={row.slot}
                      state={state}
                      disabled={base === 'disabled' || base === 'booked' || base === 'unavailable'}
                      label={cellLabel(dayIdx, row.slot, state)}
                      mode={mode}
                      armed={armedAnchor?.day === dayIdx && armedAnchor?.slot === row.slot}
                      onDown={onCellDown}
                      onEnter={onCellEnter}
                      onTap={onCellTap}
                    />
                  );
                })}
              </Fragment>
            ))}
          </div>

          {events && events.length > 0 ? (
            <div className={styles.eventLayer}>
              {events.map((event) => {
                const rowStart = event.startSlot - firstSlot + 2;
                const rowEnd = event.endSlot - firstSlot + 2;
                if (rowEnd <= rowStart) return null;
                return (
                  <div
                    key={event.id}
                    className={cx(
                      styles.event,
                      eventToneClass(event.tone),
                      event.interactive && styles.eventInteractive,
                    )}
                    data-tone={event.tone ?? 'mine'}
                    style={{
                      gridColumn: event.dayIndex + 2,
                      gridRow: `${rowStart} / ${rowEnd}`,
                    }}
                    {...(event.interactive
                      ? {}
                      : { role: 'note', 'aria-label': event.label })}
                  >
                    <div className={styles.eventInner}>{event.content}</div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
